import React from "react";
import { motion } from "framer-motion";
import { RotateCcw, Brain } from "lucide-react";

const ProblemSection = () => {
  return (
    <section className="relative py-28 px-6" data-testid="section-problem">
      <div className="max-w-7xl mx-auto">
        <div className="section-label">The problem</div>

        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="headline mt-4 text-4xl sm:text-5xl lg:text-[88px] max-w-4xl"
        >
          Language apps <em>forget</em> you.
        </motion.h2>

        <p className="mt-8 max-w-xl text-[17px] leading-[1.6] text-whisper">
          They celebrate today. They reset tomorrow. You start over, every time.
        </p>

        {/* Two big product visuals side by side */}
        <div className="mt-16 grid lg:grid-cols-2 gap-5">
          {/* Before */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="glass rounded-3xl p-8 relative overflow-hidden min-h-[340px]"
            data-testid="problem-card-before"
          >
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">Normal app · Monday</div>
              <RotateCcw size={14} className="text-whisper-muted" />
            </div>
            <div className="mt-6 space-y-3">
              {["Streak +1", "Lesson complete", "Score 82%"].map((t) => (
                <div key={t} className="hairline rounded-xl px-4 py-3 text-[14px] text-whisper">{t}</div>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-3 font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
              <span className="w-8 h-[1px] bg-panel/20" />
              <span>Tuesday</span>
              <span className="w-8 h-[1px] bg-panel/20" />
            </div>
            <div className="mt-4 p-5 rounded-2xl border border-dashed border-panel/15 text-[14.5px] text-whisper leading-[1.55]">
              "Welcome back! Let's start from the beginning."
            </div>
          </motion.div>

          {/* After */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="node-card edge-glow p-8 relative overflow-hidden min-h-[340px]"
            data-testid="problem-card-after"
          >
            <div className="relative z-10 flex items-center justify-between">
              <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-aura-cyan">AURA · Monday</div>
              <Brain size={14} className="text-aura-cyan" />
            </div>
            <div className="relative z-10 mt-6 space-y-3">
              {[
                "Past subjunctive · noted",
                "Hesitation · market vocabulary",
                "Goal · B2 by July",
              ].map((t) => (
                <div key={t} className="hairline rounded-xl px-4 py-3 text-[14px] text-ink/90 bg-aura-cyan/[0.03]">
                  {t}
                </div>
              ))}
            </div>
            <div className="relative z-10 mt-8 flex items-center gap-3 font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
              <span className="w-8 h-[1px] bg-aura-cyan/30" />
              <span>Tuesday</span>
              <span className="w-8 h-[1px] bg-aura-cyan/30" />
            </div>
            <div className="relative z-10 mt-4 p-5 rounded-2xl border border-aura-cyan/20 bg-aura-cyan/[0.04] text-[14.5px] text-ink/95 leading-[1.55]">
              "Let's pick up where you froze yesterday — the market scene."
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
